import { useContext, useState } from 'react';
import { Box, Button, useToast } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { MeContext } from '../../../context/MeContext';
import { MeContextState } from '../../../store/types/account';
import {
  useLazyGetPassTemplateQuery,
  usePublishPassTemplateMutation,
} from '../../../store/redux/api/templateApi';
import { mapApiResponseToTemplateState } from './utils/mapApiResponseToTemplateState';
import {
  validateTemplateSave,
  SaveValidationResult,
  ValidationIssue,
} from './utils/templateSaveValidation';
import { sectionFocusUrl } from './utils/sectionFocus';
import { TemplateState } from '../../../store/types/template';
import { useTemplateLimitReached } from '../../../hooks/useTemplateLimitReached';
import { useApiErrorMessages } from '../../../hooks/useApiErrorMessages';
import CustomToast from '../../../common/CustomToast';
import TemplateSaveValidationModal from './modals/TemplateSaveValidationModal';
import ConfirmModal from '../../../common/ConfirmModal';
import PublishLimitReachedModal from './PublishLimitReachedModal';

interface PublishTemplateButtonProps {
  previewData: {
    uuid: string;
    templateName?: string;
    [key: string]: unknown;
  };
}

/**
 * The "Publish template" button shown on draft cards in the list. The server
 * re-validates from the stored record, so no payload is sent; the client-side
 * check only exists to explain issues before the round trip.
 */
const PublishTemplateButton = ({ previewData }: PublishTemplateButtonProps) => {
  const toast = useToast();
  const navigate = useNavigate();
  const { state: meStateRaw } = useContext(MeContext) as {
    state: MeContextState;
  };
  const localisationEnabled = meStateRaw?.localisationEnabled ?? false;
  const { isLimitReached } = useTemplateLimitReached();
  const { getErrorMessages } = useApiErrorMessages();

  const [fetchTemplate] = useLazyGetPassTemplateQuery();
  const [publishPassTemplate] = usePublishPassTemplateMutation();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [blockedOpen, setBlockedOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saveValidation, setSaveValidation] =
    useState<SaveValidationResult | null>(null);

  const editUrl = `/pass-templates/edit/${previewData.uuid}`;

  const showPublishError = (err: unknown) => {
    const messages = getErrorMessages(err);
    toast({
      render: ({ onClose }) => (
        <CustomToast
          status="error"
          title="Failed to publish template"
          description={
            messages.length === 1
              ? messages[0]
              : messages.length > 1
                ? messages
                : undefined
          }
          onClose={onClose}
        />
      ),
    });
  };

  const doPublish = async () => {
    setBusy(true);
    try {
      await publishPassTemplate({ id: previewData.uuid }).unwrap();
      toast({
        render: ({ onClose }) => (
          <CustomToast
            status="success"
            title="Your template is being published"
            onClose={onClose}
          />
        ),
      });
    } catch (err) {
      console.error(err);
      // The client-side limit check can be stale; the server's 402 is final.
      if ((err as { status?: number })?.status === 402) {
        setBlockedOpen(true);
        return;
      }
      showPublishError(err);
    } finally {
      setBusy(false);
    }
  };

  const handlePublishClick = () => {
    if (isLimitReached) {
      setBlockedOpen(true);
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    if (isLimitReached) {
      setConfirmOpen(false);
      setBlockedOpen(true);
      return;
    }
    setConfirmOpen(false);
    setBusy(true);
    try {
      const response = await fetchTemplate({
        id: previewData.uuid,
        includeDynamicForm: true,
      }).unwrap();
      if (!response?.data) throw new Error('Failed to load template');

      const templateState = mapApiResponseToTemplateState(
        response.data as Parameters<typeof mapApiResponseToTemplateState>[0]
      ) as TemplateState;

      const validation = validateTemplateSave(templateState, {
        localisationEnabled,
      });

      if (validation.errors.length > 0 || validation.warnings.length > 0) {
        setSaveValidation(validation);
        setBusy(false);
        return;
      }

      await doPublish();
    } catch (err) {
      console.error(err);
      setBusy(false);
      showPublishError(err);
    }
  };

  const handlePublishAnyway = async () => {
    setSaveValidation(null);
    await doPublish();
  };

  const handleIssueClick = (issue: ValidationIssue) => {
    setSaveValidation(null);
    navigate(sectionFocusUrl(editUrl, issue));
  };

  const handleEditFeatures = () => {
    setSaveValidation(null);
    navigate(editUrl);
  };

  return (
    <>
      <Button
        variant="secondaryFlat"
        w="full"
        onClick={handlePublishClick}
        isLoading={busy}
      >
        Publish template
      </Button>
      <ConfirmModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Publish this template?"
        description={
          <>
            Once published, you can start issuing passes right away. Published
            templates{' '}
            <Box as="span" fontWeight="600">
              can't be deleted
            </Box>{' '}
            or reverted to draft.
          </>
        }
        primaryButton={{
          label: 'Publish template',
          onClick: handleConfirm,
          isLoading: busy,
        }}
        secondaryButton={{
          label: 'Cancel',
          onClick: () => setConfirmOpen(false),
        }}
      />
      <PublishLimitReachedModal
        isOpen={blockedOpen}
        onClose={() => setBlockedOpen(false)}
      />
      <TemplateSaveValidationModal
        isOpen={saveValidation !== null}
        onClose={() => setSaveValidation(null)}
        onSaveAnyway={handlePublishAnyway}
        onIssueClick={handleIssueClick}
        onEditFeatures={handleEditFeatures}
        result={saveValidation ?? { errors: [], warnings: [] }}
        isSaving={busy}
        mode="publish"
      />
    </>
  );
};

export default PublishTemplateButton;
